import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Users, Trash2, RefreshCw, Shield, User, Plus, Edit2, Save, KeyRound } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { api } from "@/services/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const roleColors = {
  admin: "bg-indigo-50 text-indigo-700 border-indigo-200",
  reviewer: "bg-slate-50 text-slate-700 border-slate-200",
};

export const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [registrationTypes, setRegistrationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    role: "reviewer",
    assigned_registration_type_id: "",
    assigned_organization: "",
    approved_target_type_id: "",
    declined_target_type_id: "",
  });
  const { user: currentUser } = useAuth();

  // Reset Password dialog state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetTargetUser, setResetTargetUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const openResetDialog = (user) => {
    setResetTargetUser(user);
    setNewPassword("");
    setResetDialogOpen(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setResetLoading(true);
    try {
      await api.users.resetPassword(resetTargetUser.id, newPassword);
      toast.success(`Password reset for ${resetTargetUser.full_name}`);
      setResetDialogOpen(false);
      setNewPassword("");
      setResetTargetUser(null);
    } catch (error) {
      toast.error(error.message || "Failed to reset password");
    } finally {
      setResetLoading(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [usersData, typesData] = await Promise.all([
        api.users.list(),
        api.registrationTypes.list(),
      ]);
      setUsers(usersData);
      setRegistrationTypes(typesData);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      email: "",
      password: "",
      full_name: "",
      role: "reviewer",
      assigned_registration_type_id: "",
      assigned_organization: "",
      approved_target_type_id: "",
      declined_target_type_id: "",
    });
    setEditingUser(null);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: "",
      full_name: user.full_name,
      role: user.role,
      assigned_registration_type_id: user.assigned_registration_type_id || "",
      assigned_organization: user.assigned_organization || "",
      approved_target_type_id: user.approved_target_type_id || "",
      declined_target_type_id: user.declined_target_type_id || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingUser && (!formData.email || !formData.password || !formData.full_name)) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      if (editingUser) {
        await api.users.update(editingUser.id, {
          full_name: formData.full_name,
          role: formData.role,
          assigned_registration_type_id: formData.assigned_registration_type_id || null,
          assigned_organization: formData.assigned_organization || null,
          approved_target_type_id: formData.approved_target_type_id || null,
          declined_target_type_id: formData.declined_target_type_id || null,
        });
        toast.success("User updated successfully");
      } else {
        await api.users.create(formData);
        toast.success("User created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.message || "Failed to save user");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await api.users.delete(id);
      toast.success("User deleted");
      fetchData();
    } catch (error) {
      toast.error(error.message || "Failed to delete user");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="users-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1
              className="text-2xl font-semibold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              User Management
            </h1>
            <p className="text-slate-500 mt-1">Manage users and assign registration types for review</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-950 hover:bg-indigo-900" data-testid="add-user-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {editingUser ? "Edit User" : "Add New User"}
                </DialogTitle>
                <DialogDescription>
                  {editingUser 
                    ? "Update user details and registration type assignments." 
                    : "Create a new user and assign which registration types they can review."}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {!editingUser && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="user@example.com"
                        data-testid="user-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password *</Label>
                      <Input
                        id="password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Min 6 characters"
                        data-testid="user-password-input"
                      />
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name {!editingUser && "*"}</Label>
                  <Input
                    id="fullName"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="John Doe"
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="reviewer">Reviewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Registration Type Assignments
                  </h4>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="assignedType">Reviews This Type</Label>
                      <Select
                        value={formData.assigned_registration_type_id || "all"}
                        onValueChange={(value) => setFormData({ ...formData, assigned_registration_type_id: value === "all" ? "" : value })}
                      >
                        <SelectTrigger data-testid="user-assigned-type-select">
                          <SelectValue placeholder="All types (no filter)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All types (no filter)</SelectItem>
                          {registrationTypes.map((type) => (
                            <SelectItem key={type.id} value={type.id}>
                              {type.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">
                        User will only see registrants with this type
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="assignedOrg">
                        Organization Filter
                        <span className="ml-1 text-xs font-normal text-slate-400">(optional)</span>
                      </Label>
                      <Input
                        id="assignedOrg"
                        value={formData.assigned_organization}
                        onChange={(e) => setFormData({ ...formData, assigned_organization: e.target.value })}
                        placeholder="e.g. Plan International"
                      />
                      <p className="text-xs text-slate-500">
                        If set, reviewer only sees registrants whose organization contains this text (case-insensitive). Leave blank for all organizations.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="approvedType">When Approved → Change To</Label>
                      <Select
                        value={formData.approved_target_type_id || "global"}
                        onValueChange={(value) => setFormData({ ...formData, approved_target_type_id: value === "global" ? "" : value })}
                      >
                        <SelectTrigger data-testid="user-approved-type-select">
                          <SelectValue placeholder="Use global setting" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="global">Use global setting</SelectItem>
                          {registrationTypes.map((type) => (
                            <SelectItem key={type.id} value={type.id}>
                              {type.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="declinedType">When Declined → Change To</Label>
                      <Select
                        value={formData.declined_target_type_id || "global"}
                        onValueChange={(value) => setFormData({ ...formData, declined_target_type_id: value === "global" ? "" : value })}
                      >
                        <SelectTrigger data-testid="user-declined-type-select">
                          <SelectValue placeholder="Use global setting" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="global">Use global setting</SelectItem>
                          {registrationTypes.map((type) => (
                            <SelectItem key={type.id} value={type.id}>
                              {type.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-indigo-950 hover:bg-indigo-900" data-testid="save-user-btn">
                    <Save className="h-4 w-4 mr-2" />
                    {editingUser ? "Update" : "Create"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg border border-slate-200 p-6"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-indigo-100">
                <Shield className="h-6 w-6 text-indigo-950" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Admins</p>
                <p className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {users.filter((u) => u.role === "admin").length}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg border border-slate-200 p-6"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-slate-100">
                <User className="h-6 w-6 text-slate-600" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Reviewers</p>
                <p className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {users.filter((u) => u.role === "reviewer").length}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              All Users
            </h2>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="uppercase text-xs tracking-wider text-slate-500">Name</TableHead>
                  <TableHead className="uppercase text-xs tracking-wider text-slate-500">Email</TableHead>
                  <TableHead className="uppercase text-xs tracking-wider text-slate-500">Role</TableHead>
                  <TableHead className="uppercase text-xs tracking-wider text-slate-500">Reviews Type</TableHead>
                  <TableHead className="uppercase text-xs tracking-wider text-slate-500">Organization Filter</TableHead>
                  <TableHead className="uppercase text-xs tracking-wider text-slate-500">Approved →</TableHead>
                  <TableHead className="uppercase text-xs tracking-wider text-slate-500">Declined →</TableHead>
                  <TableHead className="uppercase text-xs tracking-wider text-slate-500">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      No users found.
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user, index) => (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="border-b border-slate-100 last:border-0"
                    >
                      <TableCell className="font-medium text-slate-900">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                            {user.role === "admin" ? (
                              <Shield className="h-4 w-4 text-indigo-950" strokeWidth={1.5} />
                            ) : (
                              <User className="h-4 w-4 text-slate-500" strokeWidth={1.5} />
                            )}
                          </div>
                          {user.full_name}
                          {user.id === currentUser?.id && (
                            <Badge variant="outline" className="ml-2 text-xs">You</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{user.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={roleColors[user.role]}>
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {user.assigned_registration_type_name || (
                          <span className="text-slate-400">All types</span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {user.assigned_organization ? (
                          <Badge className="bg-violet-50 text-violet-700 border-violet-200 font-normal">
                            {user.assigned_organization}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">All orgs</span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {user.approved_target_type_name ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            {user.approved_target_type_name}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">Global</span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {user.declined_target_type_name ? (
                          <Badge className="bg-red-50 text-red-700 border-red-200">
                            {user.declined_target_type_name}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">Global</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-slate-500 hover:text-indigo-600 hover:bg-indigo-50"
                            onClick={() => openEditDialog(user)}
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-slate-500 hover:text-amber-600 hover:bg-amber-50"
                            onClick={() => openResetDialog(user)}
                            title="Reset password"
                            data-testid={`reset-password-${user.id}`}
                          >
                            <KeyRound className="h-4 w-4" />
                          </Button>
                          {user.id !== currentUser?.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(user.id)}
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </motion.tr>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={(open) => { setResetDialogOpen(open); if (!open) { setNewPassword(""); setResetTargetUser(null); } }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Reset Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{resetTargetUser?.full_name}</strong> ({resetTargetUser?.email}).
              The user will be prompted to change it on next login.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 8 characters"
                autoComplete="new-password"
              />
              <p className="text-xs text-slate-500">Must be at least 8 characters.</p>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setResetDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                disabled={resetLoading}
              >
                {resetLoading ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <KeyRound className="h-4 w-4 mr-2" />
                )}
                Reset Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
